import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { LogOut, Users, UserPlus, FileText, ChevronRight, Check, Briefcase, PlusCircle, Calendar, Clock, AlertTriangle, MessageCircle, Settings, X, ShieldCheck, MapPin, BookOpen } from 'lucide-react';
import { FadeIn } from '../components/ui/AnimatedSection';
import AdminCalendar from '../components/admin/AdminCalendar';
import AdminProtocols from '../components/admin/AdminProtocols';

type Profile = {
  id: string;
  full_name: string;
  role: string;
  created_at: string;
  billing_details: any;
};

type Prestation = {
  id: string;
  client_id: string;
  type: string;
  package_tag: string;
  custom_name: string;
  description: string;
  gross_price: number;
  net_price: number;
  status: string;
  created_at: string;
  client?: Profile;
  scheduling_preferences?: any;
};

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [prestations, setPrestations] = useState<Prestation[]>([]);
  const [interventions, setInterventions] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [unassignmentLogs, setUnassignmentLogs] = useState<any[]>([]);
  const [activeRequest, setActiveRequest] = useState<any | null>(null);
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'create' | 'prestations' | 'create_prestation' | 'planning' | 'requests' | 'unassignments' | 'protocols' | 'settings'>('users');
  const navigate = useNavigate();

  // User Management
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // User Billing Edit States
  const [editBillingAddress, setEditBillingAddress] = useState('');
  const [editBillingPostalCode, setEditBillingPostalCode] = useState('');
  const [editBillingCity, setEditBillingCity] = useState('');
  const [editBillingVat, setEditBillingVat] = useState('');
  const [editBillingSiret, setEditBillingSiret] = useState('');
  const [editBillingIban, setEditBillingIban] = useState('');
  const [saveBillingLoading, setSaveBillingLoading] = useState(false);

  // User Form states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('client');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [siret, setSiret] = useState('');
  const [iban, setIban] = useState('');
  
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [user, setUser] = useState<any>(null);

  // Settings Form States
  const [settingsFullName, setSettingsFullName] = useState('');
  const [settingsPassword, setSettingsPassword] = useState('');
  const [settingsAddress, setSettingsAddress] = useState('');
  const [settingsPostalCode, setSettingsPostalCode] = useState('');
  const [settingsCity, setSettingsCity] = useState('');
  const [settingsVatNumber, setSettingsVatNumber] = useState('');
  const [settingsSiret, setSettingsSiret] = useState('');
  const [settingsIban, setSettingsIban] = useState('');
  const [settingsMessage, setSettingsMessage] = useState('');

  // Prestation Form States
  const [pClientId, setPClientId] = useState('');
  const [pType, setPType] = useState('forfait');
  const [pPackageTag, setPPackageTag] = useState('essentiel');
  const [pCustomName, setPCustomName] = useState('');
  const [pDescription, setPDescription] = useState('');
  const [pGrossPrice, setPGrossPrice] = useState('');
  const [pNetPrice, setPNetPrice] = useState('');
  const [pStatus, setPStatus] = useState('actif');
  
  // Scheduling Preferences Form
  const [pSlots, setPSlots] = useState<{ day: string, startTime: string, duration: number }[]>([]);
  const [pCollaboratorId, setPCollaboratorId] = useState('');

  // Intervention Form States
  const [iPrestationId, setIPrestationId] = useState('');
  const [iCollaboratorId, setICollaboratorId] = useState('');
  const [iDate, setIDate] = useState('');
  const [iTime, setITime] = useState('09:00');
  const [iDuration, setIDuration] = useState(2);

  // Edit Intervention States
  const [editingInterventionId, setEditingInterventionId] = useState<string | null>(null);
  const [editIDate, setEditIDate] = useState('');
  const [editITime, setEditITime] = useState('');
  const [editIDuration, setEditIDuration] = useState(2);
  const [editICollaboratorId, setEditICollaboratorId] = useState('');

  // Planning View & Filters
  const [planningView, setPlanningView] = useState<'list' | 'calendar'>('list');
  const [filterCollaboratorId, setFilterCollaboratorId] = useState<string>('');
  const [filterClientId, setFilterClientId] = useState<string>('');

  useEffect(() => {
    const checkAdmin = async () => {
      if (!supabase) {
        navigate('/login');
        return;
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
        
      if (!profile || profile.role !== 'admin') {
        navigate('/dashboard'); 
        return;
      }
      setSettingsFullName(profile.full_name);
      if (profile.billing_details) {
        setSettingsAddress(profile.billing_details.address || '');
        setSettingsPostalCode(profile.billing_details.postalCode || '');
        setSettingsCity(profile.billing_details.city || '');
        setSettingsVatNumber(profile.billing_details.vatNumber || '');
        setSettingsSiret(profile.billing_details.siret || '');
        if (profile.billing_details.iban) {
          setSettingsIban(`•••• •••• •••• ${profile.billing_details.iban.slice(-4)}`);
        }
      }

      setUser(session.user);
      fetchProfiles();
      fetchPrestations();
      fetchInterventions();
      fetchRequests();
      fetchUnassignments();
    };
    checkAdmin();
  }, [navigate]);

  useEffect(() => {
    let interval: any;
    if (user) {
      interval = setInterval(() => {
        fetchRequests();
        fetchUnassignments();
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [user, activeRequest]);

  const fetchProfiles = async () => {
    if (!supabase) return;
    const { data } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
    if (data) {
      setProfiles(data);
      if (data.length > 0) setPClientId(data.find((p:any) => p.role === 'client')?.id || data[0].id);
    }
    setLoading(false);
  };

  const fetchPrestations = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('prestations')
      .select('*, client:profiles(*)')
      .order('created_at', { ascending: false });
    if (data) {
      setPrestations(data as any);
      if (data.length > 0) setIPrestationId(data[0].id);
    }
  };

  const fetchInterventions = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('interventions')
      .select(`
        *,
        collaborator:profiles(*),
        prestation:prestations(custom_name, client:profiles(full_name, billing_details)),
        reports(*)
      `)
      .order('scheduled_at', { ascending: false });
    if (data) setInterventions(data);
  };

  const fetchRequests = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('client_requests')
      .select('*, client:profiles(full_name), intervention:interventions(scheduled_at, prestation:prestations(custom_name)), messages(*, sender:profiles(full_name, role))')
      .order('created_at', { ascending: false });
    if (data) {
      data.forEach(r => {
        if (r.messages) r.messages.sort((a:any, b:any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      });
      setRequests(data);

      if (activeRequest) {
        const updatedReq = data.find(r => r.id === activeRequest.id);
        if (updatedReq) setActiveRequest(updatedReq);
      }
    }
  };

  const fetchUnassignments = async () => {
    if (!supabase) return;
    const { data } = await supabase
      .from('unassignment_logs')
      .select('*, intervention:interventions(scheduled_at, duration_hours, prestation:prestations(custom_name, client:profiles(full_name))), collaborator:profiles(full_name)')
      .order('created_at', { ascending: false });
    if (data) setUnassignmentLogs(data);
  };

  const markUnassignmentAsRead = async (id: string) => {
    if (!supabase) return;
    await supabase.from('unassignment_logs').update({ read_by_admin: true }).eq('id', id);
    fetchUnassignments();
  };

  const markAsRead = async (req: any) => {
    if (!supabase || !user) return;
    const unreadMessages = (req.messages || []).filter((m: any) => !m.read_by?.includes(user.id));
    if (unreadMessages.length === 0) return;

    for (const msg of unreadMessages) {
      const updatedReadBy = [...(msg.read_by || []), user.id];
      await supabase.from('messages').update({ read_by: updatedReadBy }).eq('id', msg.id);
    }
    fetchRequests();
  };

  const unreadCount = requests.reduce((acc, req) => {
    return acc + (req.messages?.filter((m: any) => !m.read_by?.includes(user?.id) && m.sender_id !== user?.id).length || 0);
  }, 0);

  const unreadUnassignmentsCount = unassignmentLogs.filter(log => !log.read_by_admin).length;

  const updateRequestStatus = async (id: string, newStatus: string) => {
    if (!supabase) return;
    await supabase.from('client_requests').update({ status: newStatus }).eq('id', id);
    fetchRequests();
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageContent.trim() || !activeRequest || !supabase) return;
    
    setSendingMessage(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Non connecté");

      const { error } = await supabase.from('messages').insert({
        request_id: activeRequest.id,
        sender_id: session.user.id,
        content: messageContent
      });
      if (error) throw error;
      setMessageContent('');
      fetchRequests();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setSettingsMessage('');
    try {
      if (!supabase) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('billing_details')
        .eq('id', session.user.id)
        .single();

      const existingBilling = currentProfile?.billing_details || {};
      const updatedIban = settingsIban.includes('••••') ? existingBilling.iban : settingsIban;

      const newBillingDetails = {
        ...existingBilling,
        address: settingsAddress,
        postalCode: settingsPostalCode,
        city: settingsCity,
        vatNumber: settingsVatNumber,
        siret: settingsSiret,
        iban: updatedIban
      };

      const updatePayload: any = {
        billing_details: newBillingDetails
      };

      if (settingsFullName) {
        updatePayload.full_name = settingsFullName;
      }

      await supabase.from('profiles').update(updatePayload).eq('id', session.user.id);
      if (settingsPassword) {
        const { error } = await supabase.auth.updateUser({ password: settingsPassword });
        if (error) throw error;
      }
      setSettingsMessage('Profil mis à jour avec succès.');
      setSettingsPassword('');
    } catch (err: any) {
      setSettingsMessage(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleLogout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    navigate('/login');
  };

  const generatePassword = () => {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let p = "";
    for(let i=0; i<12; i++) {
      p += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(p);
  };

  const addSlot = () => {
    setPSlots([...pSlots, { day: 'Lundi', startTime: '09:00', duration: 2 }]);
  };

  const removeSlot = (index: number) => {
    setPSlots(pSlots.filter((_, i) => i !== index));
  };

  const updateSlot = (index: number, field: string, value: string | number) => {
    const newSlots = [...pSlots];
    newSlots[index] = { ...newSlots[index], [field]: value };
    setPSlots(newSlots);
  };

  const handleSelectUser = (p: Profile) => {
    if (selectedUserId === p.id) {
      setSelectedUserId(null);
    } else {
      setSelectedUserId(p.id);
      setEditBillingAddress(p.billing_details?.address || '');
      setEditBillingPostalCode(p.billing_details?.postalCode || '');
      setEditBillingCity(p.billing_details?.city || '');
      setEditBillingVat(p.billing_details?.vatNumber || '');
      setEditBillingSiret(p.billing_details?.siret || '');
      setEditBillingIban(p.billing_details?.iban ? `•••• •••• •••• ${p.billing_details.iban.slice(-4)}` : '');
    }
  };

  const handleSaveUserBilling = async (userId: string) => {
    setSaveBillingLoading(true);
    try {
      if (!supabase) throw new Error('Client Supabase non initialisé');
      const { data: currentProfile } = await supabase.from('profiles').select('billing_details').eq('id', userId).single();
      const existing = currentProfile?.billing_details || {};
      
      const updatedIban = editBillingIban.includes('••••') ? existing.iban : editBillingIban;

      const newDetails = {
        ...existing,
        address: editBillingAddress,
        postalCode: editBillingPostalCode,
        city: editBillingCity,
        vatNumber: editBillingVat,
        siret: editBillingSiret,
        iban: updatedIban
      };
      
      const { error } = await supabase.from('profiles').update({ billing_details: newDetails }).eq('id', userId);
      if (error) throw error;
      
      setProfiles(profiles.map(p => p.id === userId ? { ...p, billing_details: newDetails } : p));
      alert('Informations mises à jour.');
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaveBillingLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setFormSuccess('');

    try {
      if (!supabase) throw new Error('Client Supabase non initialisé');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Non authentifié');

      const billing_details = { address, city, postalCode, vatNumber, siret, iban };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-user`, 
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ email, password, full_name: fullName, role, billing_details })
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors de la création');

      setFormSuccess(`Le compte de ${fullName} a été créé avec succès !`);
      setEmail(''); setPassword(''); setFullName(''); setAddress(''); setCity(''); setPostalCode(''); setVatNumber(''); setSiret(''); setIban('');
      fetchProfiles();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async (targetUserId: string) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet utilisateur ? Cette action est irréversible.')) return;
    setDeleteLoading(true);
    try {
      if (!supabase) throw new Error('Client Supabase non initialisé');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Non authentifié');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-delete-user`, 
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({ targetUserId })
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur lors de la suppression');
      
      setProfiles(profiles.filter(p => p.id !== targetUserId));
      setSelectedUserId(null);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCreatePrestation = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setFormSuccess('');

    try {
      if (!supabase) throw new Error('Client Supabase non initialisé');
      
      const payload = {
        client_id: pClientId,
        type: pType,
        package_tag: pType === 'forfait' ? pPackageTag : null,
        custom_name: pCustomName,
        description: pDescription,
        gross_price: parseFloat(pGrossPrice),
        net_price: parseFloat(pNetPrice),
        status: pStatus,
        scheduling_preferences: {
          slots: pSlots
        }
      };

      if (pType === 'forfait' && pSlots.length > 0) {
        // Create prestation and get ID
        const { data: newPrestation, error: insertError } = await supabase.from('prestations').insert(payload).select().single();
        if (insertError) throw insertError;
        
        // Generate 26 weeks
        const interventionsToInsert = [];
        const today = new Date();
        const daysMap: Record<string, number> = {
          'Dimanche': 0, 'Lundi': 1, 'Mardi': 2, 'Mercredi': 3, 'Jeudi': 4, 'Vendredi': 5, 'Samedi': 6
        };

        for (let week = 0; week < 26; week++) {
          for (const slot of pSlots) {
            const targetDay = daysMap[slot.day];
            const currentDate = new Date(today);
            
            currentDate.setDate(today.getDate() + (week * 7));
            const currentDay = currentDate.getDay();
            const distance = (targetDay + 7 - currentDay) % 7;
            currentDate.setDate(currentDate.getDate() + distance);

            const [hours, minutes] = slot.startTime.split(':');
            currentDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);

            interventionsToInsert.push({
              prestation_id: newPrestation.id,
              collaborator_id: pCollaboratorId || null,
              scheduled_at: currentDate.toISOString(),
              duration_hours: slot.duration,
              status: 'a_venir'
            });
          }
        }

        if (interventionsToInsert.length > 0) {
          const { error: intError } = await supabase.from('interventions').insert(interventionsToInsert);
          if (intError) throw intError;
        }
      } else {
        const { error } = await supabase.from('prestations').insert(payload);
        if (error) throw error;
      }

      setFormSuccess(`La prestation a été créée avec succès !`);
      setPCustomName(''); setPDescription(''); setPGrossPrice(''); setPNetPrice(''); setPSlots([]); setPCollaboratorId('');
      fetchPrestations();
      fetchInterventions();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreateIntervention = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    setFormSuccess('');

    try {
      if (!supabase) throw new Error('Client Supabase non initialisé');
      
      const scheduledDateTime = new Date(`${iDate}T${iTime}:00`).toISOString();

      const { error } = await supabase.from('interventions').insert({
        prestation_id: iPrestationId,
        collaborator_id: iCollaboratorId || null,
        scheduled_at: scheduledDateTime,
        duration_hours: iDuration,
        status: 'a_venir'
      });

      if (error) throw error;

      setFormSuccess(`Intervention planifiée avec succès !`);
      fetchInterventions();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const startEditIntervention = (i: any) => {
    setEditingInterventionId(i.id);
    const date = new Date(i.scheduled_at);
    // Pad to local time correctly or just use ISO since scheduled_at is UTC
    setEditIDate(date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0'));
    setEditITime(String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0'));
    setEditIDuration(i.duration_hours || 2);
    setEditICollaboratorId(i.collaborator_id || '');
  };

  const handleSaveInterventionEdit = async (id: string) => {
    try {
      if (!supabase) return;
      const scheduledDateTime = new Date(`${editIDate}T${editITime}:00`).toISOString();
      const { error } = await supabase.from('interventions').update({
        scheduled_at: scheduledDateTime,
        duration_hours: editIDuration,
        collaborator_id: editICollaboratorId || null
      }).eq('id', id);
      if (error) throw error;
      setEditingInterventionId(null);
      fetchInterventions();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const updatePrestationStatus = async (id: string, newStatus: string) => {
    if (!supabase) return;
    await supabase.from('prestations').update({ status: newStatus }).eq('id', id);
    fetchPrestations();
  };

  const updateInterventionStatus = async (id: string, newStatus: string) => {
    if (!supabase) return;
    await supabase.from('interventions').update({ status: newStatus }).eq('id', id);
    fetchInterventions();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold-200 border-t-gold-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const clients = profiles.filter(p => p.role === 'client');
  const collaborateurs = profiles.filter(p => p.role === 'collaborateur');

  return (
    <div className="min-h-screen flex bg-cream-100 font-sans text-black-deep">
      {/* Sidebar Privée */}
      <aside className="w-72 bg-black-deep text-white shadow-2xl flex flex-col fixed h-full z-20">
        <div className="p-8 border-b border-white/10 flex flex-col items-center">
          <img src="/assets/logo_v2.webp" alt="L'Art du Ménage" className="h-16 w-auto mb-4 invert brightness-0" />
          <h2 className="font-serif tracking-widest text-lg text-gold-400">ADMINISTRATION</h2>
        </div>
        
        <nav className="flex-grow py-8 px-4 space-y-2 overflow-y-auto">
          <button onClick={() => setActiveTab('users')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'users' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><Users size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Utilisateurs</span></div>
            {activeTab === 'users' && <ChevronRight size={16} />}
          </button>
          
          <button onClick={() => setActiveTab('create')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'create' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><UserPlus size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Nouveau Compte</span></div>
            {activeTab === 'create' && <ChevronRight size={16} />}
          </button>

          <div className="pt-4 mt-4 border-t border-white/5"></div>

          <button onClick={() => setActiveTab('prestations')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'prestations' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><Briefcase size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Prestations</span></div>
            {activeTab === 'prestations' && <ChevronRight size={16} />}
          </button>
          
          <button onClick={() => setActiveTab('create_prestation')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'create_prestation' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><PlusCircle size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Ajouter Service</span></div>
            {activeTab === 'create_prestation' && <ChevronRight size={16} />}
          </button>

          <div className="pt-4 mt-4 border-t border-white/5"></div>

          <button onClick={() => setActiveTab('planning')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'planning' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><Calendar size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Planning & Rapports</span></div>
            {activeTab === 'planning' && <ChevronRight size={16} />}
          </button>

          <button onClick={() => setActiveTab('requests')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'requests' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><MessageCircle size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Messagerie Client</span>
              {unreadCount > 0 && <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>}
            </div>
            {activeTab === 'requests' && <ChevronRight size={16} />}
          </button>

          <button onClick={() => setActiveTab('unassignments')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'unassignments' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><AlertTriangle size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Désistements</span>
              {unreadUnassignmentsCount > 0 && <span className="ml-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{unreadUnassignmentsCount}</span>}
            </div>
            {activeTab === 'unassignments' && <ChevronRight size={16} />}
          </button>

          <button onClick={() => setActiveTab('protocols')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'protocols' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><BookOpen size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Protocoles Client</span></div>
            {activeTab === 'protocols' && <ChevronRight size={16} />}
          </button>

          <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center justify-between px-6 py-4 rounded-sm transition-all ${activeTab === 'settings' ? 'bg-gold-600 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}>
            <div className="flex items-center gap-3"><Settings size={18} /> <span className="font-medium text-sm tracking-wider uppercase">Paramètres</span></div>
            {activeTab === 'settings' && <ChevronRight size={16} />}
          </button>
        </nav>

        <div className="p-4 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center justify-center space-x-2 px-6 py-4 text-gray-400 hover:text-red-400 hover:bg-red-950/30 rounded-sm transition-colors text-sm uppercase tracking-wider font-bold">
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-grow ml-72 p-12 overflow-y-auto">
        <FadeIn>
          <header className="mb-12">
            <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-2">Espace Dirigeant</p>
            <h1 className="text-4xl font-serif">
              {activeTab === 'users' && 'Liste des Utilisateurs'}
              {activeTab === 'create' && 'Créer un Compte'}
              {activeTab === 'prestations' && 'Suivi des Prestations'}
              {activeTab === 'create_prestation' && 'Créer une Prestation'}
              {activeTab === 'planning' && 'Planning & Interventions'}
              {activeTab === 'requests' && 'Messagerie & Requêtes Clients'}
              {activeTab === 'settings' && 'Paramètres du Profil'}
            </h1>
          </header>
        </FadeIn>

        {activeTab === 'users' && (
          <FadeIn>
            <div className="bg-white shadow-xl border border-gray-100 rounded-sm overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h2 className="font-serif text-xl">Liste des utilisateurs</h2>
                <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{profiles.length} comptes</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-white border-b border-gray-100 text-gray-500 text-xs uppercase tracking-widest">
                    <tr>
                      <th className="px-6 py-4 font-bold">Nom Complet</th>
                      <th className="px-6 py-4 font-bold">Rôle</th>
                      <th className="px-6 py-4 font-bold">Date de création</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {profiles.map(p => (
                      <React.Fragment key={p.id}>
                        <tr onClick={() => handleSelectUser(p)} className="hover:bg-gray-50 transition-colors cursor-pointer">
                          <td className="px-6 py-4 font-medium text-black-deep flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gold-100 text-gold-600 flex items-center justify-center font-bold font-serif">
                              {p.full_name.charAt(0).toUpperCase()}
                            </div>
                            {p.full_name}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full ${p.role === 'admin' ? 'bg-black-deep text-white' : p.role === 'collaborateur' ? 'bg-blue-100 text-blue-700' : 'bg-gold-100 text-gold-700'}`}>
                              {p.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-gray-500">
                            {new Date(p.created_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </td>
                        </tr>
                        {selectedUserId === p.id && (
                          <tr className="bg-gray-50 border-b border-gray-100">
                            <td colSpan={3} className="px-6 py-6">
                              <div className="flex flex-col gap-4">
                                <div className="flex justify-between items-center">
                                  <h4 className="text-sm font-bold uppercase tracking-widest text-gold-600">Modifier les détails de facturation</h4>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleSaveUserBilling(p.id)}
                                      disabled={saveBillingLoading}
                                      className="px-4 py-2 bg-black-deep text-white hover:bg-gold-600 text-xs font-bold uppercase tracking-widest rounded transition-colors disabled:opacity-50"
                                    >
                                      {saveBillingLoading ? 'Enregistrement...' : 'Enregistrer'}
                                    </button>
                                    {p.role !== 'admin' && (
                                      <button
                                        onClick={() => handleDeleteUser(p.id)}
                                        disabled={deleteLoading}
                                        className="px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 text-xs font-bold uppercase tracking-widest rounded transition-colors disabled:opacity-50"
                                      >
                                        {deleteLoading ? 'Suppression...' : 'Supprimer'}
                                      </button>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                  <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Adresse</label>
                                    <input type="text" value={editBillingAddress} onChange={(e) => setEditBillingAddress(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm focus:outline-none focus:border-gold-500 bg-white" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Code Postal</label>
                                    <input type="text" value={editBillingPostalCode} onChange={(e) => setEditBillingPostalCode(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm focus:outline-none focus:border-gold-500 bg-white" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Ville</label>
                                    <input type="text" value={editBillingCity} onChange={(e) => setEditBillingCity(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm focus:outline-none focus:border-gold-500 bg-white" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">N° TVA</label>
                                    <input type="text" value={editBillingVat} onChange={(e) => setEditBillingVat(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm focus:outline-none focus:border-gold-500 bg-white" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">SIRET</label>
                                    <input type="text" value={editBillingSiret} onChange={(e) => setEditBillingSiret(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm focus:outline-none focus:border-gold-500 bg-white" />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider flex items-center gap-1">IBAN <ShieldCheck size={12} className="text-green-600"/></label>
                                    <input type="text" value={editBillingIban} onChange={(e) => setEditBillingIban(e.target.value)} className="w-full border border-gray-300 p-2 text-sm rounded-sm focus:outline-none focus:border-gold-500 bg-white font-mono" placeholder="Saisissez le nouvel IBAN" />
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </FadeIn>
        )}

        {activeTab === 'planning' && (
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Formulaire Planification */}
              <div className="lg:col-span-1">
                <form onSubmit={handleCreateIntervention} className="bg-white shadow-xl border border-gray-100 rounded-sm p-6">
                  <h3 className="font-serif text-lg mb-6 border-b border-gray-100 pb-4">Planifier une intervention</h3>
                  
                  {formError && <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 text-xs rounded">{formError}</div>}
                  {formSuccess && <div className="mb-4 p-3 bg-green-50 text-green-700 border border-green-200 text-xs rounded flex items-center gap-2"><Check size={14} /> {formSuccess}</div>}

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Prestation Liée</label>
                      <select required value={iPrestationId} onChange={e => setIPrestationId(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm">
                        {prestations.map(p => (
                          <option key={p.id} value={p.id}>{p.client?.full_name} - {p.custom_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Collaborateur (Optionnel)</label>
                      <select value={iCollaboratorId} onChange={e => setICollaboratorId(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm">
                        <option value="">-- À assigner plus tard --</option>
                        {collaborateurs.map(c => (
                          <option key={c.id} value={c.id}>{c.full_name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Date</label>
                        <input type="date" required value={iDate} onChange={e => setIDate(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Heure</label>
                        <input type="time" required value={iTime} onChange={e => setITime(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Durée Prévue (Heures)</label>
                      <input type="number" step="0.5" required value={iDuration} onChange={e => setIDuration(parseFloat(e.target.value))} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                    </div>

                    <button type="submit" disabled={formLoading} className="w-full mt-4 py-3 bg-black-deep text-white text-xs uppercase tracking-widest font-bold rounded-sm hover:bg-gold-600 transition-colors">
                      Ajouter au planning
                    </button>
                  </div>
                </form>
              </div>

              {/* Barre d'outils / Filtres */}
              <div className="lg:col-span-2 bg-white rounded-sm border border-gray-100 p-4 flex flex-col md:flex-row gap-4 justify-between items-center shadow-sm">
                <div className="flex gap-4 w-full md:w-auto">
                  <select 
                    value={filterCollaboratorId} 
                    onChange={(e) => setFilterCollaboratorId(e.target.value)} 
                    className="border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm flex-1"
                  >
                    <option value="">Tous les collaborateurs</option>
                    {collaborateurs.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                  </select>
                  <select 
                    value={filterClientId} 
                    onChange={(e) => setFilterClientId(e.target.value)} 
                    className="border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm flex-1"
                  >
                    <option value="">Tous les clients</option>
                    {profiles.filter(p => p.role === 'client').map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                  </select>
                </div>
                <div className="flex gap-2 w-full md:w-auto bg-gray-100 p-1 rounded-sm">
                  <button 
                    onClick={() => setPlanningView('list')} 
                    className={`flex-1 md:flex-none px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${planningView === 'list' ? 'bg-white shadow text-black-deep' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    Liste
                  </button>
                  <button 
                    onClick={() => setPlanningView('calendar')} 
                    className={`flex-1 md:flex-none px-4 py-2 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${planningView === 'calendar' ? 'bg-white shadow text-black-deep' : 'text-gray-500 hover:bg-gray-200'}`}
                  >
                    Calendrier
                  </button>
                </div>
              </div>

              {/* Vue conditionnelle (Liste ou Calendrier) */}
              <div className="lg:col-span-2 space-y-4">
                {planningView === 'list' ? (
                  <>
                  {interventions
                    .filter(i => filterCollaboratorId ? i.collaborator_id === filterCollaboratorId : true)
                    .filter(i => filterClientId ? i.prestation?.client?.id === filterClientId : true)
                    .map((i: any) => {
                      if (editingInterventionId === i.id) {
                    return (
                      <div key={i.id} className="bg-white rounded-sm border p-5 shadow-sm border-gold-300">
                         <h4 className="font-serif text-lg font-bold mb-4">Modifier l'intervention</h4>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                           <div>
                             <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Date</label>
                             <input type="date" value={editIDate} onChange={e => setEditIDate(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                           </div>
                           <div>
                             <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Heure</label>
                             <input type="time" value={editITime} onChange={e => setEditITime(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                           </div>
                           <div>
                             <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Durée (h)</label>
                             <input type="number" step="0.5" value={editIDuration} onChange={e => setEditIDuration(parseFloat(e.target.value))} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm" />
                           </div>
                           <div>
                             <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Collaborateur</label>
                             <select value={editICollaboratorId} onChange={e => setEditICollaboratorId(e.target.value)} className="w-full border border-gray-300 p-2 text-sm focus:outline-none focus:border-gold-500 rounded-sm">
                               <option value="">-- Non assigné --</option>
                               {profiles.filter(p => p.role === 'collaborateur').map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                             </select>
                           </div>
                         </div>
                         <div className="flex gap-2 justify-end">
                           <button onClick={() => setEditingInterventionId(null)} className="px-4 py-2 text-xs uppercase tracking-widest font-bold border border-gray-300 rounded hover:bg-gray-50">Annuler</button>
                           <button onClick={() => handleSaveInterventionEdit(i.id)} className="px-4 py-2 text-xs uppercase tracking-widest font-bold bg-black-deep text-white rounded hover:bg-gold-600">Enregistrer</button>
                         </div>
                      </div>
                    );
                  }

                  const date = new Date(i.scheduled_at);
                  const hasReport = i.reports && i.reports.length > 0;
                  const report = hasReport ? i.reports[0] : null;

                  return (
                    <div key={i.id} className={`bg-white rounded-sm border p-5 shadow-sm relative overflow-hidden ${i.status === 'termine' ? 'border-gray-200' : 'border-gold-300'}`}>
                      {i.status === 'en_cours' && <div className="absolute top-0 left-0 w-1 h-full bg-gold-500"></div>}
                      
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-1 rounded-sm uppercase">
                              {date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock size={12}/> {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} ({i.duration_hours}h)
                            </span>
                          </div>
                          <h4 className="font-serif text-lg font-bold">{i.prestation?.client?.full_name}</h4>
                          <p className="text-sm text-gray-600 mb-2">{i.prestation?.custom_name}</p>
                          <div className="flex flex-col gap-1 mt-1 mb-3">
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                              <Users size={12}/> Assigné à : {i.collaborator ? <span className="font-bold text-black-deep">{i.collaborator.full_name}</span> : <span className="text-red-500">Non assigné</span>}
                            </p>
                            {i.prestation?.client?.billing_details?.address && (
                              <p className="text-xs text-gray-400 flex items-center gap-1">
                                <MapPin size={12}/> {i.prestation.client.billing_details.address}, {i.prestation.client.billing_details.city}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex flex-col gap-2 items-end">
                          <select 
                            value={i.status} 
                            onChange={(e) => updateInterventionStatus(i.id, e.target.value)}
                            className={`px-3 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold-500 
                              ${i.status === 'en_cours' ? 'bg-green-100 text-green-700' : 
                                i.status === 'termine' ? 'bg-gray-100 text-gray-700' :
                                i.status === 'annule' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                              }`}
                          >
                            <option value="a_venir">À venir</option>
                            <option value="en_cours">En cours</option>
                            <option value="termine">Terminé</option>
                            <option value="annule">Annulé</option>
                          </select>
                          <button onClick={() => startEditIntervention(i)} className="text-[10px] uppercase tracking-wider font-bold text-gold-600 hover:text-gold-700 underline">
                            Modifier
                          </button>
                        </div>
                      </div>

                      {hasReport && (
                        <div className="mt-4 pt-4 border-t border-gray-100 bg-gray-50 -mx-5 px-5 -mb-5 pb-5">
                          <h5 className="text-xs uppercase tracking-widest font-bold text-gold-600 mb-3 flex items-center gap-2"><FileText size={14}/> Rapport Collaborateur</h5>
                          <div className="space-y-2 text-sm">
                            {report.problems_encountered && (
                              <p><span className="text-red-600 font-bold flex items-center gap-1"><AlertTriangle size={12}/> Problèmes :</span> {report.problems_encountered}</p>
                            )}
                            {report.special_tasks && (
                              <p><span className="text-gold-600 font-bold">Tâches Spéciales :</span> {report.special_tasks}</p>
                            )}
                            {report.next_time_focus && (
                              <p><span className="text-blue-600 font-bold">Prochaine Fois :</span> {report.next_time_focus}</p>
                            )}
                            {report.photos && report.photos.length > 0 && (
                              <div className="flex gap-2 mt-2">
                                {report.photos.map((url: string, idx: number) => (
                                  <a key={idx} href={url} target="_blank" rel="noreferrer" className="w-12 h-12 rounded bg-gray-200 overflow-hidden border border-gray-300 hover:opacity-80 transition-opacity block">
                                    <img src={url} alt="Preuve" className="w-full h-full object-cover" />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                </>
                ) : (
                  <AdminCalendar 
                    interventions={interventions
                      .filter(i => filterCollaboratorId ? i.collaborator_id === filterCollaboratorId : true)
                      .filter(i => filterClientId ? i.prestation?.client?.id === filterClientId : true)
                    } 
                  />
                )}
              </div>
            </div>
          </FadeIn>
        )}

        {/* --- Tab Prestations --- */}
        {activeTab === 'prestations' && (
           <FadeIn>
           <div className="bg-white shadow-xl border border-gray-100 rounded-sm overflow-hidden">
             <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
               <h2 className="font-serif text-xl">Toutes les prestations</h2>
               <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{prestations.length} prestations</span>
             </div>
             <div className="overflow-x-auto">
               <table className="w-full text-left text-sm">
                 <thead className="bg-white border-b border-gray-100 text-gray-500 text-xs uppercase tracking-widest">
                   <tr>
                     <th className="px-6 py-4 font-bold">Client</th>
                     <th className="px-6 py-4 font-bold">Prestation</th>
                     <th className="px-6 py-4 font-bold">Planification</th>
                     <th className="px-6 py-4 font-bold">Statut</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-100">
                   {prestations.map(p => (
                     <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                       <td className="px-6 py-4 font-medium text-black-deep">
                         {p.client?.full_name || 'Client inconnu'}
                       </td>
                       <td className="px-6 py-4">
                         <div className="flex flex-col">
                           <span className="font-bold">{p.custom_name}</span>
                           <span className="text-xs text-gray-400 uppercase tracking-widest mt-1">
                             {p.type === 'forfait' ? `Forfait ${p.package_tag}` : 'One-Shot'}
                           </span>
                         </div>
                       </td>
                       <td className="px-6 py-4 text-gray-500 text-xs">
                         {p.scheduling_preferences?.hours_per_week ? `${p.scheduling_preferences.hours_per_week}h / sem.` : 'Ponctuel'}
                         <br/>
                         {p.scheduling_preferences?.preferred_days?.join(', ')}
                       </td>
                       <td className="px-6 py-4">
                         <select 
                           value={p.status} 
                           onChange={(e) => updatePrestationStatus(p.id, e.target.value)}
                           className={`px-3 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-gold-500 
                             ${p.status === 'actif' || p.status === 'en_cours' ? 'bg-green-100 text-green-700' : 
                               p.status === 'termine' ? 'bg-gray-100 text-gray-700' :
                               p.status === 'suspendu' ? 'bg-yellow-100 text-yellow-700' :
                               p.status === 'annule' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                             }`}
                         >
                           <option value="actif">Actif</option>
                           <option value="a_venir">À venir</option>
                           <option value="en_cours">En cours</option>
                           <option value="termine">Terminé</option>
                           <option value="suspendu">Suspendu</option>
                           <option value="annule">Annulé</option>
                         </select>
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
           </div>
         </FadeIn>
        )}

        {/* --- Formulaire Creation Prestation --- */}
        {activeTab === 'create_prestation' && (
          <FadeIn>
            <form onSubmit={handleCreatePrestation} className="bg-white shadow-xl border border-gray-100 rounded-sm p-8 max-w-3xl">
              
              {formError && <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 text-sm rounded">{formError}</div>}
              {formSuccess && <div className="mb-6 p-4 bg-green-50 text-green-700 border border-green-200 text-sm rounded flex items-center gap-2"><Check size={18} /> {formSuccess}</div>}

              <div className="space-y-8">
                <div>
                  <h3 className="text-sm uppercase tracking-widest text-gold-600 font-bold mb-4 flex items-center gap-2"><Briefcase size={16}/> Informations Générales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Sélection du Client</label>
                      <select required value={pClientId} onChange={e => setPClientId(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent">
                        <option value="" disabled>-- Choisir un client --</option>
                        {clients.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Type de prestation</label>
                      <select value={pType} onChange={e => {
                        setPType(e.target.value);
                        setPStatus(e.target.value === 'forfait' ? 'actif' : 'a_venir');
                      }} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent">
                        <option value="forfait">Forfait Récurrent</option>
                        <option value="oneshot">Intervention Ponctuelle (One-shot)</option>
                      </select>
                    </div>
                    {pType === 'forfait' && (
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Forfait de base (Tag)</label>
                        <select value={pPackageTag} onChange={e => setPPackageTag(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent">
                          <option value="essentiel">Essentiel</option>
                          <option value="signature">Signature</option>
                          <option value="intendance">Intendance</option>
                        </select>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Nom de la prestation (Visible client)</label>
                      <input type="text" required value={pCustomName} onChange={e => setPCustomName(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="Ex: Forfait Signature + Repassage" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Description / Ajustements</label>
                      <textarea rows={3} value={pDescription} onChange={e => setPDescription(e.target.value)} className="w-full border border-gray-300 p-2 focus:outline-none focus:border-gold-500 rounded" placeholder="Détails de la prestation sur-mesure..."></textarea>
                    </div>
                  </div>
                </div>

                {/* NOUVEAU: Préférences de Planification */}
                {pType === 'forfait' && (
                  <div>
                    <h3 className="text-sm uppercase tracking-widest text-gold-600 font-bold mb-4 flex items-center gap-2 mt-8"><Calendar size={16}/> Planification & Créneaux (Génération sur 6 mois)</h3>
                    
                    <div className="mb-6">
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Collaborateur assigné par défaut (Optionnel)</label>
                      <select value={pCollaboratorId} onChange={e => setPCollaboratorId(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent">
                        <option value="">-- Non assigné --</option>
                        {profiles.filter(p => p.role === 'collaborateur').map(c => (
                          <option key={c.id} value={c.id}>{c.full_name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">Ce collaborateur sera affecté à toutes les interventions générées.</p>
                    </div>

                    <div className="space-y-4">
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Créneaux horaires hebdomadaires</label>
                      {pSlots.map((slot, index) => (
                        <div key={index} className="flex flex-wrap items-center gap-4 bg-gray-50 p-4 rounded border border-gray-100">
                          <select value={slot.day} onChange={e => updateSlot(index, 'day', e.target.value)} className="border-b border-gray-300 py-1 bg-transparent focus:outline-none focus:border-gold-500 text-sm">
                            {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                          <input type="time" value={slot.startTime} onChange={e => updateSlot(index, 'startTime', e.target.value)} className="border-b border-gray-300 py-1 bg-transparent focus:outline-none focus:border-gold-500 text-sm" />
                          <div className="flex items-center gap-2">
                            <input type="number" step="0.5" value={slot.duration} onChange={e => updateSlot(index, 'duration', parseFloat(e.target.value))} className="border-b border-gray-300 py-1 bg-transparent focus:outline-none focus:border-gold-500 text-sm w-16 text-center" />
                            <span className="text-xs text-gray-500">heures</span>
                          </div>
                          <button type="button" onClick={() => removeSlot(index)} className="text-red-500 hover:text-red-700 ml-auto">
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      <button type="button" onClick={addSlot} className="text-xs font-bold uppercase tracking-widest text-gold-600 hover:text-gold-700 flex items-center gap-1 mt-2">
                        <PlusCircle size={14}/> Ajouter un créneau
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="text-sm uppercase tracking-widest text-gold-600 font-bold mb-4 flex items-center gap-2 mt-8"><FileText size={16}/> Tarification & Statut</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pType === 'forfait' ? (
                      <div>
                        <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Forfait au mois (€)</label>
                        <input type="number" step="0.01" required value={pGrossPrice} onChange={e => {
                          setPGrossPrice(e.target.value);
                          setPNetPrice(e.target.value ? (parseFloat(e.target.value) / 2).toString() : '');
                        }} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="Ex: 400.00" />
                      </div>
                    ) : (
                      <>
                        <div>
                          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Tarif Brut (€)</label>
                          <input type="number" step="0.01" required value={pGrossPrice} onChange={e => setPGrossPrice(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="Ex: 50.00" />
                        </div>
                        <div>
                          <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Tarif Net (Après crédit d'impôt)</label>
                          <input type="number" step="0.01" required value={pNetPrice} onChange={e => setPNetPrice(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="Ex: 25.00" />
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Statut Initial</label>
                      <select value={pStatus} onChange={e => setPStatus(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent">
                        <option value="actif">Actif</option>
                        <option value="a_venir">À venir</option>
                        <option value="en_cours">En cours</option>
                        <option value="termine">Terminé</option>
                        <option value="suspendu">Suspendu</option>
                        <option value="annule">Annulé</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="pt-8 flex justify-end">
                  <button type="submit" disabled={formLoading} className="px-8 py-4 bg-black-deep text-white text-sm uppercase tracking-widest font-bold hover:bg-gold-600 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg">
                    {formLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                    Ajouter la prestation
                  </button>
                </div>
              </div>
            </form>
          </FadeIn>
        )}

        {/* --- MESSAGERIE CLIENT --- */}
        {activeTab === 'requests' && (
          <FadeIn>
            <div className="bg-white shadow-xl border border-gray-100 rounded-sm overflow-hidden flex" style={{ height: '70vh' }}>
              {/* Left Sidebar: List of requests */}
              <div className="w-1/3 border-r border-gray-100 flex flex-col bg-gray-50">
                <div className="p-4 border-b border-gray-100 bg-white">
                  <h3 className="font-serif text-lg">Conversations & Requêtes</h3>
                  <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">{requests.length} requêtes</p>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {requests.length === 0 ? (
                    <p className="p-4 text-sm text-gray-400 italic text-center mt-4">Aucune requête en attente.</p>
                  ) : (
                    requests.map(req => (
                      <button 
                        key={req.id} 
                        onClick={() => { setActiveRequest(req); markAsRead(req); }}
                        className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-100 transition-colors ${activeRequest?.id === req.id ? 'bg-white border-l-4 border-l-gold-500' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs font-bold uppercase text-gray-500 truncate mr-2">
                            {req.client?.full_name}
                          </span>
                          <span className={`flex-shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded ${req.status === 'traite' ? 'bg-green-100 text-green-700' : 'bg-gold-100 text-gold-700'}`}>
                            {req.status === 'traite' ? 'Traité' : 'En cours'}
                          </span>
                        </div>
                        <p className="text-[10px] uppercase text-gray-400 mb-2">
                          {req.type === 'message_general' ? 'Général' : req.type === 'demande_speciale' ? 'Spécial' : 'Problème'}
                        </p>
                        <p className="text-sm font-medium text-black-deep line-clamp-1">{req.content}</p>
                        <p className="text-xs text-gray-400 mt-2">{new Date(req.created_at).toLocaleDateString('fr-FR')}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Right Pane: Active Chat */}
              <div className="w-2/3 flex flex-col bg-white">
                {!activeRequest ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400 italic text-sm">
                    Sélectionnez une conversation pour afficher les messages.
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                    {/* Chat Header */}
                    <div className="p-4 border-b border-gray-100 bg-white flex justify-between items-center">
                      <div>
                        <h3 className="font-serif text-lg">{activeRequest.client?.full_name}</h3>
                        <p className="text-xs text-gray-500">
                          {activeRequest.type === 'message_general' ? 'Question Générale' : activeRequest.type === 'demande_speciale' ? 'Demande Spéciale' : 'Problème Intervention'}
                          {activeRequest.intervention && ` • Intervention du ${new Date(activeRequest.intervention.scheduled_at).toLocaleDateString('fr-FR')} (${activeRequest.intervention.prestation?.custom_name})`}
                        </p>
                      </div>
                      <div>
                        {activeRequest.status === 'en_attente' ? (
                          <button 
                            onClick={() => updateRequestStatus(activeRequest.id, 'traite')}
                            className="px-4 py-2 bg-black-deep text-white text-[10px] uppercase tracking-widest font-bold rounded-sm hover:bg-gold-600 transition-colors flex items-center gap-2"
                          >
                            <Check size={12}/> Marquer Traité
                          </button>
                        ) : (
                          <span className="text-xs uppercase tracking-widest font-bold text-green-600 flex items-center gap-1"><Check size={14}/> Traité</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Messages List */}
                    <div className="flex-1 p-6 overflow-y-auto bg-gray-50 space-y-6">
                      {/* Initial Request Message */}
                      <div className="flex flex-col items-start">
                        <div className="bg-white border border-gray-100 text-black-deep p-4 rounded-lg rounded-tl-none max-w-[80%] shadow-sm">
                          <p className="text-sm whitespace-pre-wrap">{activeRequest.content}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 mt-1">{activeRequest.client?.full_name} • {new Date(activeRequest.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}</span>
                      </div>

                      {/* Replies */}
                      {activeRequest.messages?.map((msg: any) => {
                        const isMe = msg.sender?.role === 'admin';
                        return (
                          <div key={msg.id} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                            <div className={`p-4 rounded-lg shadow-sm max-w-[80%] ${isMe ? 'bg-black-deep text-white rounded-tr-none' : 'bg-white border border-gray-100 rounded-tl-none'}`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <span className="text-[10px] text-gray-400 mt-1">
                              {isMe ? 'Vous (Admin)' : msg.sender?.full_name} • {new Date(msg.created_at).toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Chat Input */}
                    {activeRequest.status !== 'traite' && (
                      <div className="p-4 bg-white border-t border-gray-100">
                        <form onSubmit={handleSendMessage} className="flex gap-2">
                          <textarea 
                            rows={1}
                            value={messageContent}
                            onChange={e => setMessageContent(e.target.value)}
                            placeholder="Votre réponse..."
                            className="flex-1 border border-gray-300 p-3 text-sm focus:outline-none focus:border-gold-500 rounded-sm resize-none"
                            onKeyDown={e => {
                              if(e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage(e as any);
                              }
                            }}
                          ></textarea>
                          <button 
                            type="submit" 
                            disabled={sendingMessage || !messageContent.trim()}
                            className="px-6 bg-gold-600 text-white text-xs font-bold uppercase tracking-widest hover:bg-gold-500 transition-colors rounded-sm disabled:opacity-50"
                          >
                            {sendingMessage ? '...' : 'Envoyer'}
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </FadeIn>
        )}

        {activeTab === 'create' && (
          <FadeIn>
             <form onSubmit={handleCreateUser} className="bg-white shadow-xl border border-gray-100 rounded-sm p-8 max-w-3xl">
              {formError && <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 text-sm rounded">{formError}</div>}
              {formSuccess && <div className="mb-6 p-4 bg-green-50 text-green-700 border border-green-200 text-sm rounded flex items-center gap-2"><Check size={18} /> {formSuccess}</div>}

              <div className="space-y-8">
                <div>
                  <h3 className="text-sm uppercase tracking-widest text-gold-600 font-bold mb-4 flex items-center gap-2"><UserPlus size={16}/> Identifiants & Profil</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Nom Complet</label>
                      <input type="text" required value={fullName} onChange={e => setFullName(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="Ex: Jean Dupont" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Rôle</label>
                      <select value={role} onChange={e => setRole(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent cursor-pointer">
                        <option value="client">Client</option>
                        <option value="collaborateur">Collaborateur</option>
                        <option value="admin">Administrateur</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Adresse E-mail</label>
                      <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="email@domaine.com" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Mot de passe par défaut</label>
                      <div className="flex gap-2">
                        <input type="text" required value={password} onChange={e => setPassword(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 font-mono text-sm" placeholder="***" />
                        <button type="button" onClick={generatePassword} className="px-3 py-1 bg-gray-100 text-xs uppercase tracking-widest hover:bg-gray-200 transition-colors rounded">Générer</button>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm uppercase tracking-widest text-gold-600 font-bold mb-4 flex items-center gap-2 mt-8"><FileText size={16}/> Détails de Facturation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Adresse Postale Complète</label>
                      <input type="text" value={address} onChange={e => setAddress(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="123 Avenue des Champs-Élysées" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Code Postal</label>
                      <input type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="75008" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">Ville</label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="Paris" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">SIRET (Optionnel)</label>
                      <input type="text" value={siret} onChange={e => setSiret(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="123 456 789 00012" />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">N° TVA Intracommunautaire</label>
                      <input type="text" value={vatNumber} onChange={e => setVatNumber(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500" placeholder="FR 12 345678900" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2">IBAN</label>
                      <input type="text" value={iban} onChange={e => setIban(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 font-mono" placeholder="FR76 ...." />
                    </div>
                  </div>
                </div>

                <div className="pt-8 flex justify-end">
                  <button type="submit" disabled={formLoading} className="px-8 py-4 bg-black-deep text-white text-sm uppercase tracking-widest font-bold hover:bg-gold-600 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg">
                    {formLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                    Créer le compte
                  </button>
                </div>
              </div>
            </form>
          </FadeIn>
        )}

        {activeTab === 'unassignments' && (
          <FadeIn>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Unassignment logs */}
              <div className="bg-white shadow-xl border border-gray-100 rounded-sm p-6">
                <h2 className="font-serif text-xl mb-6 border-b border-gray-100 pb-4 flex items-center gap-2">
                  <AlertTriangle className="text-red-500" size={24} /> 
                  Historique des Désistements
                </h2>
                <div className="space-y-4">
                  {unassignmentLogs.map(log => (
                    <div key={log.id} className={`p-4 border rounded-sm ${log.read_by_admin ? 'border-gray-200 bg-gray-50' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="text-xs font-bold uppercase tracking-widest text-red-600">{log.collaborator?.full_name}</span>
                          <span className="text-xs text-gray-500 ml-2">{new Date(log.created_at).toLocaleString('fr-FR')}</span>
                        </div>
                        {!log.read_by_admin && (
                          <button onClick={() => markUnassignmentAsRead(log.id)} className="text-xs text-blue-600 hover:underline font-bold">Marquer comme lu</button>
                        )}
                      </div>
                      <p className="text-sm text-gray-800 mb-2"><strong>Raison :</strong> {log.reason}</p>
                      <div className="text-xs text-gray-500 p-2 bg-white rounded border border-gray-100">
                        <strong>Intervention :</strong> {log.intervention?.prestation?.client?.full_name} - {log.intervention?.prestation?.custom_name} <br/>
                        Prévue le : {new Date(log.intervention?.scheduled_at).toLocaleString('fr-FR')} ({log.intervention?.duration_hours}h)
                      </div>
                    </div>
                  ))}
                  {unassignmentLogs.length === 0 && <p className="text-sm text-gray-500">Aucun désistement enregistré.</p>}
                </div>
              </div>

              {/* Unassigned missions */}
              <div className="bg-white shadow-xl border border-gray-100 rounded-sm p-6">
                <h2 className="font-serif text-xl mb-6 border-b border-gray-100 pb-4">Missions Non Affectées</h2>
                <div className="space-y-4">
                  {interventions.filter(i => i.status === 'a_venir' && !i.collaborator_id).map(i => (
                    <div key={i.id} className="p-4 border border-gray-200 rounded-sm bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-black-deep">{i.prestation?.client?.full_name}</span>
                        <span className="text-xs font-medium text-gray-500">{new Date(i.scheduled_at).toLocaleString('fr-FR')}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{i.prestation?.custom_name}</p>
                      <div className="flex gap-2">
                        <button onClick={() => { startEditIntervention(i); setActiveTab('planning'); }} className="text-xs px-3 py-1 bg-black-deep text-white rounded hover:bg-gold-500 transition-colors">Réassigner au planning</button>
                      </div>
                    </div>
                  ))}
                  {interventions.filter(i => i.status === 'a_venir' && !i.collaborator_id).length === 0 && (
                    <p className="text-sm text-gray-500">Toutes les missions futures ont un collaborateur assigné.</p>
                  )}
                </div>
              </div>
            </div>
          </FadeIn>
        )}

        {activeTab === 'protocols' && (
          <FadeIn>
            <AdminProtocols supabase={supabase} clients={clients} />
          </FadeIn>
        )}

        {activeTab === 'settings' && (
          <FadeIn>
            <form onSubmit={handleUpdateSettings} className="bg-white shadow-xl border border-gray-100 rounded-sm p-8 max-w-xl">
              <h2 className="font-serif text-xl mb-6 border-b border-gray-100 pb-4">Mes Informations</h2>
              
              {settingsMessage && <div className={`mb-6 p-4 text-sm rounded flex items-center gap-2 ${settingsMessage.includes('succès') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {settingsMessage}
              </div>}

              <div className="space-y-6">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Nom Complet</label>
                  <input type="text" required value={settingsFullName} onChange={e => setSettingsFullName(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                </div>
                
                <h3 className="font-serif text-lg mt-8 pt-6 border-t border-gray-100 mb-4">Coordonnées & Facturation</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Adresse postale</label>
                    <input type="text" value={settingsAddress} onChange={e => setSettingsAddress(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Code Postal</label>
                    <input type="text" value={settingsPostalCode} onChange={e => setSettingsPostalCode(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Ville</label>
                    <input type="text" value={settingsCity} onChange={e => setSettingsCity(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Numéro de TVA</label>
                    <input type="text" value={settingsVatNumber} onChange={e => setSettingsVatNumber(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">SIRET / SIREN</label>
                    <input type="text" value={settingsSiret} onChange={e => setSettingsSiret(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold flex items-center gap-2">IBAN <ShieldCheck size={14} className="text-green-600"/></label>
                    <input 
                      type="text" 
                      value={settingsIban} 
                      onChange={e => setSettingsIban(e.target.value)} 
                      placeholder="Saisissez votre IBAN"
                      className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 bg-transparent font-mono text-sm" 
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Vos coordonnées bancaires sont chiffrées et stockées de manière sécurisée (conformité RGPD).</p>
                  </div>
                </div>

                <h3 className="font-serif text-lg mt-8 pt-6 border-t border-gray-100 mb-4">Sécurité</h3>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-gray-500 mb-2 font-bold">Nouveau Mot de passe (optionnel)</label>
                  <input type="password" value={settingsPassword} onChange={e => setSettingsPassword(e.target.value)} className="w-full border-b border-gray-300 py-2 focus:outline-none focus:border-gold-500 text-sm bg-transparent" placeholder="Laisser vide pour ne pas changer" />
                </div>
              </div>

              <div className="pt-8 flex justify-end">
                <button type="submit" disabled={formLoading} className="px-8 py-4 bg-black-deep text-white text-sm uppercase tracking-widest font-bold hover:bg-gold-600 transition-colors disabled:opacity-50 flex items-center gap-2 shadow-lg">
                  {formLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>}
                  Mettre à jour
                </button>
              </div>
            </form>
          </FadeIn>
        )}

      </main>
    </div>
  );
}
